/**
 * Access token acquisition (§16).
 *
 * Identity is WorkOS's job, not ours: the browser authenticates against AuthKit
 * and receives a signed JWT, which we attach to every API request. Services
 * verify that JWT statelessly against WorkOS's published keys, so nothing here
 * ever talks to our backend about who the user is.
 *
 * The SDK owns the hard parts — the redirect round trip, storing the session,
 * and refreshing the token before it expires. This module exists only to adapt
 * its shape to the one `createClient` asks for.
 */
import { LoginRequiredError, useAuth } from "@workos-inc/authkit-react";
import { useCallback } from "react";

/**
 * Returns the token getter to hand to `createClient`.
 *
 * The SDK throws `LoginRequiredError` when nobody is signed in; the API client
 * asks for `null` instead. That is not a workaround for an awkward API — the
 * two are describing different things. To the SDK, "no session" is an
 * exceptional answer to "give me a token". To us it is an ordinary state: the
 * request goes out unauthenticated and the API answers 401, which is exactly
 * what a signed-out user should see. Every other failure — the network, a
 * refresh that genuinely broke — is a real error and keeps propagating.
 *
 * Must be called inside <AuthKitProvider>.
 */
export function useAccessToken(): () => Promise<string | null> {
	const { getAccessToken } = useAuth();

	return useCallback(async () => {
		try {
			return await getAccessToken();
		} catch (cause) {
			if (cause instanceof LoginRequiredError) {
				return null;
			}
			throw cause;
		}
	}, [getAccessToken]);
}
